'use client';

import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';
import PersonalDashboardInteractive from './components/PersonalDashboardInteractive';
import { useAuth } from '@/contexts/AuthContext';

export default function PersonalDashboard() {
  const { user: authUser, userProfile } = useAuth();
  
  // Get real user name from auth context or fallback to mock
  const realUserName = userProfile?.full_name || userProfile?.fullName || authUser?.email?.split('@')[0] || 'User';
  
  // Mock data for frontend testing without Supabase dependency
  const MOCK_DASHBOARD_DATA = {
    user: {
      name: realUserName
    },
    readingStats: {
      currentBook: 'Gospel of John',
      progressPercentage: 65,
      chaptersRead: 45,
      daysStreak: 7
    },
    devotionalPlan: {
      id: 'devotional-1',
      slug: 'walking-in-faith',
      title: '30 Days of Prayer',
      currentDay: 12,
      totalDays: 30,
      todayReading: 'Matthew 6:9-13',
      verse: 'Matthew 6:9-13',
      reflection: 'Today we reflect on the power of prayer and how it connects us with God. Prayer is not just about asking, but about building a relationship with our Creator.',
      readingTime: 5,
      planProgress: 12,
      completed: false
    },
    prayerEntries: [
      {
        id: 1,
        date: '2025-12-01',
        title: 'Thanksgiving Prayer',
        excerpt: 'Grateful for the blessings of this week...'
      },
      {
        id: 2,
        date: '2025-11-30',
        title: 'Prayer for Guidance',
        excerpt: 'Seeking wisdom in making important decisions...'
      },
      {
        id: 3,
        date: '2025-11-29',
        title: 'Family Prayer',
        excerpt: 'Praying for peace and unity in our home...'
      }
    ],
    notes: [
      {
        id: 1,
        verse: 'John 3:16',
        note: 'God\'s love demonstrated through sacrifice',
        date: '2025-12-01'
      },
      {
        id: 2,
        verse: 'Romans 8:28',
        note: 'All things work together for good',
        date: '2025-11-30'
      }
    ],
    goals: [
      {
        id: 1,
        title: 'Complete New Testament',
        progress: 65,
        target: 100,
        deadline: '2025-12-31'
      },
      {
        id: 2,
        title: 'Memorize 10 Verses',
        progress: 6,
        target: 10,
        deadline: '2025-12-15'
      }
    ],
    achievements: [
      {
        id: 1,
        title: '7 Day Reading Streak',
        icon: 'fire',
        unlockedDate: '2025-12-01'
      },
      {
        id: 2,
        title: 'First Commentary Completed',
        icon: 'book',
        unlockedDate: '2025-11-28'
      }
    ],
    recommendations: [
      {
        id: 1,
        title: 'The Purpose Driven Life',
        description: 'Discover God\'s amazing plan for your life with Rick Warren\'s inspirational guide',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_173f0f34a-1764675522793.png",
        alt: 'The Purpose Driven Life audiobook cover showing a peaceful landscape at sunrise',
        duration: '8h 30m',
        category: 'Audiobook'
      },
      {
        id: 2,
        title: 'Mere Christianity',
        description: 'C.S. Lewis\'s timeless exploration of Christian faith and theology',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_17f30f355-1764675523667.png",
        alt: 'Mere Christianity audiobook cover featuring a cross with warm lighting',
        duration: '6h 45m',
        category: 'Audiobook'
      },
      {
        id: 3,
        title: 'Daily Strength Devotional',
        description: 'Find daily encouragement and spiritual growth through powerful devotions',
        image: "https://images.unsplash.com/photo-1590766310253-0e5bd8da71d4",
        alt: 'Daily Strength devotional book cover with open Bible and morning light',
        duration: '15 min/day',
        category: 'Devotional'
      },
      {
        id: 4,
        title: 'Praying God\'s Word',
        description: 'Transform your prayer life with Scripture-based prayers',
        image: "https://images.unsplash.com/photo-1567781549213-16f8ac8488f0",
        alt: 'Prayer book cover showing hands folded in prayer with soft lighting',
        duration: '5h 20m',
        category: 'Audiobook'
      }
    ],
    scheduledEvents: [
      {
        id: 1,
        title: 'Sunday Service',
        date: '2025-12-03T10:00:00Z',
        type: 'service',
        day: 3,
        time: '10:00 AM',
        description: 'Weekly worship service'
      },
      {
        id: 2,
        title: 'Bible Study Group',
        date: '2025-12-04T19:00:00Z',
        type: 'study',
        day: 4,
        time: '7:00 PM',
        description: 'Group study on Acts'
      },
      {
        id: 3,
        title: 'Prayer Meeting',
        date: '2025-12-05T18:30:00Z',
        type: 'prayer',
        day: 5,
        time: '6:30 PM',
        description: 'Community prayer gathering'
      },
      {
        id: 4,
        title: 'Daily Devotional',
        date: '2025-12-08T08:00:00Z',
        type: 'devotional',
        day: 8,
        time: '8:00 AM',
        description: 'Morning devotion - Walking in Faith',
        devotionalSlug: 'walking-in-faith'
      },
      {
        id: 5,
        title: 'Daily Devotional',
        date: '2025-12-15T08:00:00Z',
        type: 'devotional',
        day: 15,
        time: '8:00 AM',
        description: 'Morning devotion - Grace Abounds',
        devotionalSlug: 'grace-abounds'
      },
      {
        id: 6,
        title: 'Daily Devotional',
        date: '2025-12-22T08:00:00Z',
        type: 'devotional',
        day: 22,
        time: '8:00 AM',
        description: 'Morning devotion - Finding Peace',
        devotionalSlug: 'finding-peace'
      }
    ],
    bookmarks: {
      audiobooks: [
        {
          id: 1,
          title: 'The Book of John Audio',
          currentChapter: 'Chapter 15',
          progress: 71,
          remainingTime: '2h 15m',
          image: "https://images.unsplash.com/photo-1519114050654-96babf933689",
          alt: 'The Book of John audiobook cover with golden cross design'
        },
        {
          id: 2,
          title: 'Psalms for Peace',
          currentChapter: 'Psalm 23',
          progress: 45,
          remainingTime: '4h 30m',
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_19135efe9-1764675520421.png",
          alt: 'Psalms for Peace audiobook cover showing peaceful landscape'
        },
        {
          id: 3,
          title: 'Proverbs of Wisdom',
          currentChapter: 'Proverbs 3',
          progress: 23,
          remainingTime: '6h 45m',
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecdf6206-1766550212682.png",
          alt: 'Proverbs of Wisdom audiobook cover with ancient scroll design'
        }
      ],
      books: [
        {
          id: 1,
          title: 'Understanding the Gospels',
          currentChapter: 'Chapter 8: The Parables',
          progress: 65,
          highlights: 12,
          notes: 8,
          image: "https://images.unsplash.com/photo-1611625618313-68b87aaa0626",
          alt: 'Understanding the Gospels book cover with four gospel symbols'
        },
        {
          id: 2,
          title: 'Acts of the Apostles Study',
          currentChapter: 'Chapter 5: The Early Church',
          progress: 42,
          highlights: 8,
          notes: 5,
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bceb34aa-1764675520743.png",
          alt: 'Acts of the Apostles study book cover showing disciples gathering'
        },
        {
          id: 3,
          title: 'Revelation Commentary',
          currentChapter: 'Chapter 2: Letters to Churches',
          progress: 18,
          highlights: 4,
          notes: 2,
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_11067a2fb-1764675525174.png",
          alt: 'Revelation Commentary book cover with prophetic imagery'
        }
      ]
    },
    syncStats: {
      notesCount: 45,
      highlightsCount: 78
    }
  };

  // Use real auth user data or fallback to mock
  const MOCK_USER = authUser || {
    id: 'mock-user-123',
    email: 'user@example.com',
    created_at: '2025-01-01T00:00:00Z'
  };

  const MOCK_USER_PROFILE = userProfile || {
    id: 'mock-user-123',
    username: realUserName?.replace(/\s+/g, '') || 'User',
    full_name: realUserName,
    avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
    preferences: {
      theme: 'light',
      notifications: true
    }
  };

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading delay for realistic frontend testing
    const loadMockData = async () => {
      setLoading(true);
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 800));
      setDashboardData(MOCK_DASHBOARD_DATA);
      setLoading(false);
    };

    loadMockData();
  }, []);

  const handleDataUpdate = async () => {
    // Simulate data refresh
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setDashboardData(MOCK_DASHBOARD_DATA);
    setLoading(false);
  };

  // Show loading spinner while data is loading
  if (loading || !dashboardData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <Icon name="ArrowPathIcon" size={48} className="mx-auto text-primary animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Loading Dashboard</h2>
            <p className="text-muted-foreground">Please wait while we load your data...</p>
          </div>
        </main>
      </div>
    );
  }

  // Show dashboard with data - removed Suspense wrapper as components are now directly imported
  return (
    <>
      <Header />
      <main className="pt-16">
        <PersonalDashboardInteractive
          initialData={dashboardData}
          user={MOCK_USER}
          userProfile={MOCK_USER_PROFILE}
          onDataUpdate={handleDataUpdate}
        />
      </main>
    </>
  );
}